/**
 * MarketplacePatterns - Step 1.2 Implementation
 * Comprehensive marketplace pattern management system
 * Connects admin dashboard to 2W billing app's marketplace detection system
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Spinner,
  Banner,
  EmptyState,
  DataTable,
  TextField,
  Select,
  ButtonGroup,
  Tooltip,
  Modal,
  FormLayout,
  Tabs,
  Checkbox,
} from '@shopify/polaris';
import {
  SearchIcon,
  RefreshIcon,
  PlusIcon,
  EditIcon,
  DeleteIcon,
} from '@shopify/polaris-icons';

import {
  useMarketplacePatterns,
  useCreateMarketplacePattern,
  useUpdateMarketplacePattern,
  useDeleteMarketplacePattern,
} from '../utils/queries';
import { PageSizeSelect } from '../utils/table';
import apiClient from '../utils/api';

/**
 * Override actions, as stored on BrandMarketplaceOverride.overrideAction. The server serves
 * the authoritative list from /brand-marketplace-overrides/options; these are the labels.
 *
 * Only DISABLE changes behaviour on its own today — it stores a never-matching pattern, so
 * the brand stops matching that retailer's global patterns entirely. The other four all
 * resolve to "this brand's patterns replace the global ones for this retailer" in the
 * detection service; they are recorded so the intent behind each row is auditable.
 */
const OVERRIDE_ACTION_LABELS = {
  ADD: 'Add — a retailer the global patterns do not cover',
  EXTEND: 'Extend — cover extra tag spellings for this retailer',
  REPLACE: 'Replace — use this pattern instead of the global one',
  MODIFY: 'Modify — same retailer, different matching for this brand',
  DISABLE: 'Disable — this brand opts out of the global pattern',
};

/** Actions that act on an existing global pattern and so require one to be chosen. */
const ACTIONS_NEEDING_GLOBAL_PATTERN = ['EXTEND', 'REPLACE', 'DISABLE', 'MODIFY'];

/**
 * The row count both tables on this screen OPEN on, and the seed for their two page-size
 * states. It used to be the size itself — `useState(25)` with no setter and no control.
 *
 * Each table keeps its OWN state seeded from here: the global patterns and the per-brand
 * overrides are separate lists, read by separate requests, and one shared value would resize
 * a table on the other tab as a side effect of choosing a size here.
 */
const PAGE_SIZE = 25;

/**
 * Descriptive labels for the PatternType values. This is a LABEL map, not the value list - the
 * values themselves now come from GET /brand-marketplace-overrides/options, which serves the
 * same PATTERN_TYPES constant the server validator uses
 * (brand-marketplace-override-service.js:36).
 */
const PATTERN_TYPE_LABELS = {
  CONTAINS: 'Contains - pattern appears anywhere in tag',
  EXACT: 'Exact Match - tag must exactly match pattern',
  STARTS_WITH: 'Starts With - tag begins with pattern',
  ENDS_WITH: 'Ends With - tag ends with pattern',
  WORD_BOUNDARY: 'Word Boundary - pattern as complete word',
  REGEX: 'Regular Expression - advanced pattern matching',
};

const emptyOverrideForm = {
  brandId: '',
  overrideAction: 'ADD',
  globalPatternId: '',
  marketplaceName: '',
  displayName: '',
  patternType: 'CONTAINS',
  patternValue: '',
  priority: 1,
  isActive: true,
  isCaseSensitive: false,
  reason: '',
  isTemporary: false,
  expiresAt: '',
};

export default function MarketplacePatterns() {
  // Filter and search state.
  // Removed: `marketplaceFilter`/`setMarketplaceFilter` (the setter was never called and the
  // value was never read - state for a filter this tab does not have) and the `navigate` from
  // useNavigate (never called). The single `pageSize` — `useState(25)` with no setter
  // destructured, i.e. state that could not change — is now one state per table, both seeded
  // from the PAGE_SIZE constant above and both driven by the shared rows-per-page control.
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Global patterns: pages SERVER-side, so the chosen size goes out as the API's `limit`
  // (see queryParams below). Slicing the response would show ten of the twenty-five rows the
  // server sent and label it "Page 1 of 1".
  const [patternsPageSize, setPatternsPageSize] = useState(PAGE_SIZE);

  // Modal state
  const [showPatternModal, setShowPatternModal] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    marketplaceName: '',
    displayName: '',
    patternType: 'CONTAINS',
    patternValue: '',
    priority: 1,
    isActive: true,
    isCaseSensitive: false,
    category: 'primary',
    description: '',
  });

  // Tab state — 0: global patterns (every brand), 1: per-brand overrides
  const [selectedTab, setSelectedTab] = useState(0);

  // Per-brand override state
  const [overrides, setOverrides] = useState([]);
  const [overridesPagination, setOverridesPagination] = useState({ totalCount: 0, totalPages: 1 });
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [overridesError, setOverridesError] = useState(null);
  const [overridePage, setOverridePage] = useState(1);
  const [overrideBrandFilter, setOverrideBrandFilter] = useState('');
  const [overrideActionFilter, setOverrideActionFilter] = useState('');
  const [overrideStatusFilter, setOverrideStatusFilter] = useState('');
  const [overrideSearch, setOverrideSearch] = useState('');
  // Per-brand overrides: its own size, and also server-side — fetchOverrides sends it as
  // `limit` and reads totalPages back from the response.
  const [overridesPageSize, setOverridesPageSize] = useState(PAGE_SIZE);

  const [brands, setBrands] = useState([]);
  const [allGlobalPatterns, setAllGlobalPatterns] = useState([]);
  /**
   * Enum values and the priority range, fetched from GET /brand-marketplace-overrides/options
   * (marketplace.controller.js:292-296), which serves PATTERN_TYPES / OVERRIDE_ACTIONS /
   * PRIORITY_MIN / PRIORITY_MAX straight from the constants the server validator uses.
   *
   * Starts EMPTY. It used to be seeded with a hand-copied duplicate of all three, and the fetch
   * is `.catch(() => null)`, so a failing options call left the invented copy in place
   * permanently - while that copy drove the pattern-type select, the action select, the priority
   * bounds AND the client-side validation that decides whether a save is even attempted. These
   * values decide which retailer an order is attributed to and therefore which commission rate
   * is billed, so a stale local guess is a mis-billed order. Empty means the form is disabled
   * until the real values arrive (see optionsLoaded).
   */
  const [overrideOptions, setOverrideOptions] = useState({
    patternTypes: [],
    overrideActions: [],
    priorityRange: null,
  });
  const [optionsError, setOptionsError] = useState(false);

  // A new row count starts again at page 1 on both tables: the old page number means nothing
  // against a new size, and on the last page it would request a page that no longer exists.
  const handlePatternsPageSizeChange = useCallback((next) => {
    setPatternsPageSize(next);
    setCurrentPage(1);
  }, []);

  const handleOverridesPageSizeChange = useCallback((next) => {
    setOverridesPageSize(next);
    setOverridePage(1);
  }, []);

  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [savingOverride, setSavingOverride] = useState(false);
  const [overrideForm, setOverrideForm] = useState(emptyOverrideForm);

  // Build query parameters
  const queryParams = useMemo(() => ({
    page: currentPage,
    limit: patternsPageSize,
    ...(searchQuery && { marketplace: searchQuery }),
    ...(statusFilter && { isActive: statusFilter }),
  }), [currentPage, patternsPageSize, searchQuery, statusFilter]);

  // Fetch data
  const {
    data: patternsResponse,
    isLoading,
    error,
    refetch,
    isPlaceholderData,
  } = useMarketplacePatterns(queryParams);

  // Mutations
  const createPattern = useCreateMarketplacePattern();
  const updatePattern = useUpdateMarketplacePattern();
  const deletePattern = useDeleteMarketplacePattern();

  // Robust API response parser with error handling
  const parseApiResponse = (response) => {
    if (!response) {
      return { patterns: [], pagination: { totalCount: 0, totalPages: 1 } };
    }
    
    try {
      // Handle direct array response
      if (Array.isArray(response)) {
        return {
          patterns: response,
          pagination: { totalCount: response.length, totalPages: 1 }
        };
      }
      
      // Handle object response - try common field names
      if (typeof response === 'object') {
        const patterns = response.patterns || response.data || response.items || [];
        const pagination = response.pagination || response.meta || response.paging || {};
        
        // Ensure patterns is an array
        const safePatterns = Array.isArray(patterns) ? patterns : [];
        
        // Calculate safe pagination values
        const totalCount = pagination.totalCount || pagination.total || pagination.totalItems || safePatterns.length || 0;
        const totalPages = pagination.totalPages || pagination.pages || Math.ceil(totalCount / patternsPageSize) || 1;
        
        return {
          patterns: safePatterns,
          pagination: { totalCount, totalPages }
        };
      }
      
      // Fallback for unexpected response format
      return { patterns: [], pagination: { totalCount: 0, totalPages: 1 } };
    } catch (error) {
      // Only log parsing errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplacePatterns: Error parsing API response:', error, response);
      }
      return { patterns: [], pagination: { totalCount: 0, totalPages: 1 } };
    }
  };
  
  // Extract data from response using robust parser
  const { patterns, pagination } = parseApiResponse(patternsResponse);

  // Have the server's enums arrived?
  const optionsLoaded = overrideOptions.patternTypes.length > 0 && !!overrideOptions.priorityRange;

  // Priority bounds, from the server's PRIORITY_MIN/PRIORITY_MAX. The global-pattern form used
  // to clamp to a hardcoded 1-10 in two places while the override form on the same page already
  // read these - priority decides retailer attribution and therefore the commission rate billed,
  // so the two halves must not disagree.
  const priorityMin = overrideOptions.priorityRange?.min ?? 1;
  const priorityMax = overrideOptions.priorityRange?.max ?? 10;

  // Pattern type options, built from the server's PATTERN_TYPES with local labels. This was a
  // second hand-copied duplicate of the enum.
  const patternTypeOptions = useMemo(
    () => overrideOptions.patternTypes.map((value) => ({
      label: PATTERN_TYPE_LABELS[value] || value,
      value,
    })),
    [overrideOptions.patternTypes]
  );

  // Pattern category options.
  //
  // DELIBERATELY LEFT HARDCODED - there is nothing to fetch. GlobalMarketplacePattern.category is
  // a free-text `String @default("primary") @db.VarChar(50)` (schema.prisma:595); the create
  // route defaults it to 'primary' and validates nothing (marketplace.controller.js:138, :168),
  // and the options endpoint does not expose a category list. This console is therefore the only
  // definition of the taxonomy that exists anywhere.
  // BACKEND FOLLOW-UP: promote category to an enum (or add it to
  // /brand-marketplace-overrides/options) so this list can be driven by the server like the rest.
  const categoryOptions = [
    { label: 'Primary', value: 'primary' },
    { label: 'Alias', value: 'alias' },
    { label: 'Legacy', value: 'legacy' },
  ];

  // Handlers
  const handleAddPattern = useCallback(() => {
    setEditingPattern(null);
    setFormData({
      marketplaceName: '',
      displayName: '',
      patternType: 'CONTAINS',
      patternValue: '',
      priority: 1,
      isActive: true,
      isCaseSensitive: false,
      category: 'primary',
      description: '',
    });
    setShowPatternModal(true);
  }, []);

  const handleEditPattern = useCallback((pattern) => {
    setEditingPattern(pattern);
    setFormData({
      marketplaceName: pattern.marketplaceName || '',
      displayName: pattern.displayName || '',
      patternType: pattern.patternType || 'CONTAINS',
      patternValue: pattern.patternValue || '',
      priority: pattern.priority || 1,
      isActive: pattern.isActive !== false,
      isCaseSensitive: pattern.isCaseSensitive || false,
      category: pattern.category || 'primary',
      description: pattern.description || '',
    });
    setShowPatternModal(true);
  }, []);

  const handleSavePattern = useCallback(async () => {
    try {
      // Validate required fields
      if (!formData.marketplaceName?.trim()) {
        throw new Error('Marketplace name is required');
      }
      if (!formData.patternValue?.trim()) {
        throw new Error('Pattern value is required');
      }
      
      // Validate REGEX patterns
      if (formData.patternType === 'REGEX') {
        try {
          new RegExp(formData.patternValue);
        } catch (regexError) {
          throw new Error('Invalid regular expression pattern');
        }
      }
      
      // Sanitize and validate form data
      const sanitizedData = {
        ...formData,
        marketplaceName: formData.marketplaceName.trim(),
        displayName: formData.displayName?.trim() || '',
        patternValue: formData.patternValue.trim(),
        // Clamp to the SERVER's range, not a hardcoded 1-10.
        priority: Math.max(priorityMin, Math.min(priorityMax, formData.priority || priorityMin)),
        description: formData.description?.trim() || ''
      };
      
      if (editingPattern) {
        await updatePattern.mutateAsync({
          id: editingPattern.id,
          ...sanitizedData,
        });
      } else {
        await createPattern.mutateAsync(sanitizedData);
      }
      setShowPatternModal(false);
    } catch (error) {
      // Only log detailed errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplacePatterns: Error saving pattern:', error);
      }
      // Show user-friendly error message
      alert(error.message || 'Failed to save pattern. Please check your input and try again.');
    }
  }, [editingPattern, formData, createPattern, updatePattern]);

  const handleDeletePattern = useCallback(async (patternId) => {
    if (window.confirm('Are you sure you want to delete this marketplace pattern?')) {
      try {
        await deletePattern.mutateAsync(patternId);
      } catch (error) {
        // Told to the user, not just the console.
        //
        // This was swallowed unless NODE_ENV was development, so a failed delete looked like
        // nothing happening: the row stayed and the only trace was a stack in the server log.
        // The mutation refreshes the list on error, so by the time this shows, the row has
        // already gone if it was simply stale.
        const msg = String(error?.message || '');
        alert(/not found|404/i.test(msg)
          ? 'That pattern no longer exists — the list has been refreshed.'
          : msg || 'Failed to delete the pattern.');
        if (process.env.NODE_ENV === 'development') {
          console.error('MarketplacePatterns: Error deleting pattern:', error);
        }
      }
    }
  }, [deletePattern]);

  // ==========================================
  // PER-BRAND OVERRIDES — data loading
  // ==========================================

  const fetchOverrides = useCallback(async () => {
    setOverridesLoading(true);
    setOverridesError(null);
    try {
      const result = await apiClient.getBrandMarketplaceOverrides({
        page: overridePage,
        limit: overridesPageSize,
        ...(overrideBrandFilter && { brandId: overrideBrandFilter }),
        ...(overrideActionFilter && { overrideAction: overrideActionFilter }),
        ...(overrideStatusFilter && { isActive: overrideStatusFilter }),
        ...(overrideSearch && { marketplace: overrideSearch }),
      });
      setOverrides(Array.isArray(result?.overrides) ? result.overrides : []);
      setOverridesPagination({
        totalCount: result?.pagination?.totalCount || 0,
        totalPages: result?.pagination?.totalPages || 1,
      });
    } catch (err) {
      setOverridesError(err.message || 'Failed to load per-brand overrides');
      setOverrides([]);
    } finally {
      setOverridesLoading(false);
    }
  }, [overridePage, overridesPageSize, overrideBrandFilter, overrideActionFilter, overrideStatusFilter, overrideSearch]);

  useEffect(() => {
    if (selectedTab === 1) {
      fetchOverrides();
    }
  }, [selectedTab, fetchOverrides]);

  /**
   * Brands, the full global pattern list and the server's enum list back the override form.
   * The global patterns are fetched separately from the paginated table above because the
   * form needs every pattern to pick from, not just the current page.
   */
  // Options are fetched for BOTH tabs, not just the overrides tab. The global-pattern form on
  // tab 0 needs the same pattern-type list and the same priority range, and it previously used
  // its own hardcoded copies because this effect returned early unless selectedTab === 1.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const optionResult = await apiClient.getBrandMarketplaceOverrideOptions().catch(() => null);
      if (cancelled) return;

      if (optionResult?.patternTypes || optionResult?.overrideActions || optionResult?.priorityRange) {
        setOverrideOptions({
          patternTypes: Array.isArray(optionResult.patternTypes) ? optionResult.patternTypes : [],
          overrideActions: Array.isArray(optionResult.overrideActions) ? optionResult.overrideActions : [],
          priorityRange: optionResult.priorityRange || null,
        });
        setOptionsError(false);
      } else {
        // Say so instead of silently falling back to a local guess.
        setOptionsError(true);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (selectedTab !== 1) return;
    let cancelled = false;

    (async () => {
      const [brandResult, patternResult] = await Promise.all([
        apiClient.getBrands({ limit: 500 }).catch(() => null),
        apiClient.getMarketplacePatterns({ limit: 200 }).catch(() => null),
      ]);
      if (cancelled) return;

      const brandList = brandResult?.brands || brandResult?.data || (Array.isArray(brandResult) ? brandResult : []);
      setBrands(Array.isArray(brandList) ? brandList : []);

      const patternList = patternResult?.patterns || (Array.isArray(patternResult) ? patternResult : []);
      setAllGlobalPatterns(Array.isArray(patternList) ? patternList : []);
    })();

    return () => { cancelled = true; };
  }, [selectedTab]);

  const brandOptions = useMemo(() => ([
    { label: 'Select a brand…', value: '' },
    ...brands.map((brand) => ({ label: brand.name || brand.shopDomain || brand.id, value: brand.id })),
  ]), [brands]);

  const brandFilterOptions = useMemo(() => ([
    { label: 'All brands', value: '' },
    ...brands.map((brand) => ({ label: brand.name || brand.shopDomain || brand.id, value: brand.id })),
  ]), [brands]);

  const overrideActionOptions = useMemo(() => (
    overrideOptions.overrideActions.map((action) => ({
      label: OVERRIDE_ACTION_LABELS[action] || action,
      value: action,
    }))
  ), [overrideOptions.overrideActions]);

  const overrideActionFilterOptions = useMemo(() => ([
    { label: 'All actions', value: '' },
    ...overrideOptions.overrideActions.map((action) => ({ label: action, value: action })),
  ]), [overrideOptions.overrideActions]);

  const globalPatternOptions = useMemo(() => ([
    { label: 'None — this is a new retailer (Add only)', value: '' },
    ...allGlobalPatterns.map((pattern) => ({
      label: `${pattern.marketplaceName} — ${pattern.patternType} "${pattern.patternValue}"`,
      value: pattern.id,
    })),
  ]), [allGlobalPatterns]);

  const overridePatternTypeOptions = useMemo(() => (
    overrideOptions.patternTypes.map((type) => {
      const known = patternTypeOptions.find((option) => option.value === type);
      return known || { label: type, value: type };
    })
  ), [overrideOptions.patternTypes]);

  const isDisableAction = overrideForm.overrideAction === 'DISABLE';
  const needsGlobalPattern = ACTIONS_NEEDING_GLOBAL_PATTERN.includes(overrideForm.overrideAction);

  // ==========================================
  // PER-BRAND OVERRIDES — handlers
  // ==========================================

  const handleAddOverride = useCallback(() => {
    setEditingOverride(null);
    setOverrideForm({ ...emptyOverrideForm, brandId: overrideBrandFilter || '' });
    setShowOverrideModal(true);
  }, [overrideBrandFilter]);

  const handleEditOverride = useCallback((override) => {
    setEditingOverride(override);
    setOverrideForm({
      brandId: override.brandId || '',
      overrideAction: override.overrideAction || 'ADD',
      globalPatternId: override.globalPatternId || '',
      marketplaceName: override.marketplaceName || '',
      displayName: override.displayName || '',
      patternType: override.patternType || 'CONTAINS',
      // A DISABLE row stores a never-matching sentinel, not a real pattern. Showing it in the
      // form would invite someone to edit it into something that matches, which would quietly
      // un-disable the override.
      patternValue: override.overrideAction === 'DISABLE' ? '' : (override.patternValue || ''),
      priority: override.priority || 1,
      isActive: override.isActive !== false,
      isCaseSensitive: override.isCaseSensitive || false,
      reason: override.reason || '',
      isTemporary: override.isTemporary || false,
      expiresAt: override.expiresAt ? String(override.expiresAt).slice(0, 10) : '',
    });
    setShowOverrideModal(true);
  }, []);

  /**
   * Builds the payload field by field rather than spreading the form, because the API rejects
   * a DISABLE that carries a pattern — deliberately, so a disable can never be a silent no-op.
   */
  const handleSaveOverride = useCallback(async () => {
    // Same reason as the Priority field: priorityRange is null until the options request
    // lands, and destructuring it threw before the first validation line could run.
    const min = priorityMin;
    const max = priorityMax;
    try {
      if (!overrideForm.brandId) throw new Error('Brand is required');
      if (!overrideForm.overrideAction) throw new Error('Override action is required');

      const disabling = overrideForm.overrideAction === 'DISABLE';

      if (ACTIONS_NEEDING_GLOBAL_PATTERN.includes(overrideForm.overrideAction) && !overrideForm.globalPatternId) {
        throw new Error(
          `"${overrideForm.overrideAction}" acts on an existing global pattern, so a global pattern must be selected. ` +
          'Use "Add" to introduce a retailer the global patterns do not cover.'
        );
      }
      if (!disabling && !overrideForm.patternValue?.trim()) {
        throw new Error('Pattern value is required');
      }
      if (!disabling && !overrideForm.globalPatternId && !overrideForm.marketplaceName?.trim()) {
        throw new Error('Retailer name is required when no global pattern is selected');
      }
      if (disabling && !overrideForm.reason?.trim()) {
        throw new Error('A reason is required when disabling a global pattern for a brand');
      }
      if (overrideForm.isTemporary && !overrideForm.expiresAt) {
        throw new Error('A temporary override needs an expiry date');
      }
      const priority = parseInt(overrideForm.priority, 10);
      if (!Number.isInteger(priority) || priority < min || priority > max) {
        throw new Error(`Priority must be a whole number between ${min} and ${max}`);
      }
      if (!disabling && overrideForm.patternType === 'REGEX') {
        try {
          new RegExp(overrideForm.patternValue);
        } catch (regexError) {
          throw new Error('Invalid regular expression pattern');
        }
      }

      const payload = {
        overrideAction: overrideForm.overrideAction,
        globalPatternId: overrideForm.globalPatternId || null,
        displayName: overrideForm.displayName?.trim() || undefined,
        priority,
        isActive: overrideForm.isActive,
        isCaseSensitive: overrideForm.isCaseSensitive,
        reason: overrideForm.reason?.trim() || undefined,
        isTemporary: overrideForm.isTemporary,
        expiresAt: overrideForm.isTemporary ? overrideForm.expiresAt : null,
      };

      // The server derives marketplaceName from the chosen global pattern; sending a
      // different one is rejected, so only send it when there is no pattern to derive from.
      if (!overrideForm.globalPatternId && overrideForm.marketplaceName?.trim()) {
        payload.marketplaceName = overrideForm.marketplaceName.trim();
      }
      if (!disabling) {
        payload.patternType = overrideForm.patternType;
        payload.patternValue = overrideForm.patternValue.trim();
      }

      setSavingOverride(true);
      if (editingOverride) {
        await apiClient.updateBrandMarketplaceOverride(editingOverride.id, payload);
      } else {
        await apiClient.createBrandMarketplaceOverride({ ...payload, brandId: overrideForm.brandId });
      }
      setShowOverrideModal(false);
      setEditingOverride(null);
      await fetchOverrides();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplacePatterns: Error saving brand override:', err);
      }
      alert(err.message || 'Failed to save override. Please check your input and try again.');
    } finally {
      setSavingOverride(false);
    }
  }, [overrideForm, editingOverride, fetchOverrides, priorityMin, priorityMax]);

  const handleDeleteOverride = useCallback(async (override) => {
    const label = `${override.marketplaceName} (${override.overrideAction})`;
    if (!window.confirm(
      `Delete the ${label} override for ${override.brand?.name || override.brandId}? ` +
      'This brand will go back to the global patterns, which may change how its orders are attributed.'
    )) {
      return;
    }
    try {
      await apiClient.deleteBrandMarketplaceOverride(override.id);
      await fetchOverrides();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplacePatterns: Error deleting brand override:', err);
      }
      alert(err.message || 'Failed to delete override.');
    }
  }, [fetchOverrides]);

  // Helper functions
  /** Wireframe MP_CAT: primary -> success, alias -> info, legacy -> neutral. */
  const getCategoryTone = (category) => {
    switch (String(category || '').toLowerCase()) {
      case 'primary': return 'success';
      case 'alias': return 'info';
      default: return undefined; // legacy and anything unrecognised read as neutral
    }
  };

  const cap = (s) => {
    const v = String(s || '');
    return v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : v;
  };

  /**
   * An inactive pattern is dimmed, as the wireframe has it. DataTable has no per-row
   * style, so each cell is wrapped — the alternative is reading a switched-off pattern
   * as a live one, which is the mistake the dimming exists to prevent.
   */
  const dimIfInactive = (isActive, cells) =>
    (isActive ? cells : cells.map((c, i) => (
      <span key={i} style={{ opacity: 0.62 }}>{c}</span>
    )));

  const getPatternTypeTone = (type) => {
    switch (type) {
      case 'EXACT': return 'success';
      case 'CONTAINS': return 'info';
      case 'REGEX': return 'warning';
      default: return 'subdued';
    }
  };

  const getPatternHelpText = (type) => {
    switch (type) {
      case 'EXACT': return 'Tag must match exactly (e.g., "target")';
      case 'CONTAINS': return 'Tag must contain pattern (e.g., "target" matches "target-order-123")';
      case 'STARTS_WITH': return 'Tag must start with pattern (e.g., "target-")';
      case 'ENDS_WITH': return 'Tag must end with pattern (e.g., "-target")';
      case 'WORD_BOUNDARY': return 'Pattern as complete word (e.g., "target" but not "targetplus")';
      case 'REGEX': return 'Regular expression pattern (use carefully)';
      default: return '';
    }
  };

  // Safe table data rendering with error handling
  const tableRows = patterns.map((pattern) => {
    try {
      if (!pattern || typeof pattern !== 'object') {
        return null;
      }
      
      const isActive = pattern.isActive !== false;
      return dimIfInactive(isActive, [
        pattern.marketplaceName || 'Unknown',
        pattern.patternValue || '',
        <Badge tone={getPatternTypeTone(pattern.patternType || 'CONTAINS')}>{pattern.patternType || 'CONTAINS'}</Badge>,
        // The wireframe renders the category as a toned, capitalised badge, not raw text.
        <Badge tone={getCategoryTone(pattern.category)}>{cap(pattern.category || 'primary')}</Badge>,
        pattern.priority || 1,
        // Inactive is neutral, not critical — a switched-off pattern is not an error.
        <Badge tone={isActive ? 'success' : undefined}>
          {isActive ? 'Active' : 'Inactive'}
        </Badge>,
        <ButtonGroup variant="segmented">
          <Tooltip content="Edit Pattern">
            <Button
              size="micro"
              variant="tertiary"
              icon={EditIcon}
              onClick={() => handleEditPattern(pattern)}
              disabled={!pattern.id}
            />
          </Tooltip>
          <Tooltip content="Delete Pattern">
            <Button
              size="micro"
              variant="tertiary"
              tone="critical"
              icon={DeleteIcon}
              onClick={() => handleDeletePattern(pattern.id)}
              disabled={!pattern.id}
            />
          </Tooltip>
        </ButtonGroup>,
      ]);
    } catch (error) {
      // Only log rendering errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplacePatterns: Error rendering pattern row:', error, pattern);
      }
      return null;
    }
  }).filter(Boolean); // Remove any null entries

  /** DISABLE is the destructive-looking one, so it reads as a warning rather than a status. */
  const getOverrideActionTone = (action) => {
    switch (action) {
      case 'DISABLE': return 'warning';
      case 'REPLACE': return 'attention';
      case 'ADD': return 'success';
      default: return 'info';
    }
  };

  const formatExpiry = (override) => {
    if (!override.expiresAt) return override.isTemporary ? 'Temporary (no date)' : 'Never';
    const date = new Date(override.expiresAt);
    if (Number.isNaN(date.getTime())) return 'Never';
    return date.toLocaleDateString();
  };

  const overrideRows = overrides.map((override) => {
    try {
      if (!override || typeof override !== 'object') return null;

      const isActive = override.isActive !== false;
      const disabling = override.overrideAction === 'DISABLE';

      return dimIfInactive(isActive, [
        override.brand?.name || override.brandId || 'Unknown brand',
        override.displayName || override.marketplaceName || 'Unknown',
        <Badge key="action" tone={getOverrideActionTone(override.overrideAction)}>{override.overrideAction || 'ADD'}</Badge>,
        // A disable row stores a never-matching sentinel; printing "(?!)" in a table would
        // read as a corrupt pattern rather than as an intentional opt-out.
        disabling
          ? <Text key="pattern" tone="subdued" variant="bodySm">Matches nothing (opted out)</Text>
          : override.patternValue || '',
        disabling
          ? <Badge key="type" tone="warning">DISABLED</Badge>
          : <Badge key="type" tone={getPatternTypeTone(override.patternType || 'CONTAINS')}>{override.patternType || 'CONTAINS'}</Badge>,
        override.priority || 1,
        formatExpiry(override),
        <Badge key="status" tone={isActive ? 'success' : undefined}>{isActive ? 'Active' : 'Inactive'}</Badge>,
        <ButtonGroup key="actions" variant="segmented">
          <Tooltip content={override.reason ? `Reason: ${override.reason}` : 'Edit override'}>
            <Button
              size="micro"
              variant="tertiary"
              icon={EditIcon}
              onClick={() => handleEditOverride(override)}
              disabled={!override.id}
            />
          </Tooltip>
          <Tooltip content="Delete override">
            <Button
              size="micro"
              variant="tertiary"
              tone="critical"
              icon={DeleteIcon}
              onClick={() => handleDeleteOverride(override)}
              disabled={!override.id}
            />
          </Tooltip>
        </ButtonGroup>,
      ]);
    } catch (renderError) {
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplacePatterns: Error rendering override row:', renderError, override);
      }
      return null;
    }
  }).filter(Boolean);

  const tabs = [
    { id: 'global-patterns', content: 'Global patterns', accessibilityLabel: 'Global marketplace patterns' },
    { id: 'brand-overrides', content: 'Per-brand overrides', accessibilityLabel: 'Per-brand marketplace pattern overrides' },
  ];

  // Loading state — scoped to the global tab so a slow or failing global query cannot hide
  // the overrides tab, which is the fix for a mis-attributed brand and needs to stay reachable.
  if (selectedTab === 0 && isLoading && patterns.length === 0) {
    return (
      <Page title="Marketplace Pattern Management" fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <BlockStack gap="200">
                    <Text variant="bodyLg" fontWeight="medium">
                      Loading marketplace patterns...
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Error state — likewise scoped to the global tab.
  if (selectedTab === 0 && error) {
    return (
      <Page title="Marketplace Pattern Management" fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load marketplace patterns. {error.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
          <Layout.Section>
            <Card>
              <Box padding="300">
                <Button onClick={() => setSelectedTab(1)}>Go to per-brand overrides</Button>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Empty state
  const emptyStateMarkup = (
    <EmptyState
      heading="No marketplace patterns found"
      content="Create your first marketplace pattern to start detecting marketplaces in order tags."
      image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
      action={{
        content: 'Add Pattern',
        onAction: handleAddPattern,
      }}
    />
  );

  return (
    <Page
      title="Marketplace Pattern Management"
      fullWidth
      // The API's total, not the length of the current page of 25. pagination.totalCount is
      // already parsed just above and is what the overrides tab on the next line uses.
      subtitle={selectedTab === 0
        ? `${pagination.totalCount} global patterns configured`
        : `${overridesPagination.totalCount} per-brand overrides configured`}
      backAction={{ content: 'Dashboard', url: '/' }}
      primaryAction={selectedTab === 0
        ? {
          content: 'Add Pattern',
          icon: PlusIcon,
          onAction: handleAddPattern,
        }
        : {
          content: 'Add Override',
          icon: PlusIcon,
          onAction: handleAddOverride,
        }}
      secondaryActions={[
        {
          content: 'Refresh',
          icon: RefreshIcon,
          onAction: () => (selectedTab === 0 ? refetch() : fetchOverrides()),
          loading: selectedTab === 0 ? isLoading : overridesLoading,
        },
      ]}
    >
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
              <Box padding="400">
                <BlockStack gap="300">
                  <Text tone="subdued" variant="bodySm">
                    {selectedTab === 0
                      ? 'Global patterns decide which retailer an order is attributed to for every brand. An order that matches nothing lands in the "Others" bucket and is billed at the wrong commission rate.'
                      : 'Per-brand overrides are the escape hatch: they change detection for one brand only. Use them to cover a retailer the global patterns miss, or to opt a brand out of a global pattern that mis-attributes its orders.'}
                  </Text>
                  {/* Said where the change is made, not only where its absence shows up. A
                      pattern edited here takes effect on the next import and on nothing that
                      arrived before it, which is invisible from this screen. */}
                  <Banner tone="info">
                    Changes here apply to orders imported from now on. An order already imported
                    keeps the retailer it was attributed to, and its commission rate with it — to
                    correct those, run a billing sync with
                    <b> Force recalculation with current settings</b> for each month they fall in.
                  </Banner>
                </BlockStack>
              </Box>
            </Tabs>
          </Card>
        </Layout.Section>

        {/* Filter Bar */}
        {selectedTab === 0 && (
        <Layout.Section>
          <Card>
            <Box padding="300">
              <InlineStack gap="300" align="start" blockAlign="end">
                <Box width="300px">
                  <TextField
                    label="Search"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search marketplace or pattern..."
                    prefix={<SearchIcon />}
                    clearButton
                    onClearButtonClick={() => setSearchQuery('')}
                    autoComplete="off"
                  />
                </Box>
                
                <Box width="150px">
                  <Select
                    label="Status"
                    options={[
                      { label: 'All', value: '' },
                      { label: 'Active', value: 'true' },
                      { label: 'Inactive', value: 'false' },
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                  />
                </Box>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>
        )}

        {/* Main Patterns Table */}
        {selectedTab === 0 && (
        <Layout.Section>
          <Card>
            {/* THE TABLE KEEPS ITS COLUMNS WHEN IT HAS NO ROWS.

                With no patterns the whole table used to be replaced by the empty state, so a
                fresh install saw no column names at all — no Priority, no Pattern Type, no
                sign that a pattern is more than a string. DataTable offers no `emptyState` or
                `itemCount`, but it derives a cell's colSpan from the row's length against
                headings.length (DataTable.js getColSpan), so a row of ONE cell spans the
                table. The empty state moves into that row, wording and Add Pattern action
                unchanged. */}
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
              headings={[
                'Marketplace',
                'Pattern Value',
                'Pattern Type',
                'Category',
                'Priority',
                'Status',
                'Actions',
              ]}
              rows={patterns.length === 0 ? [[emptyStateMarkup]] : tableRows}
              loading={isLoading && isPlaceholderData}
            />

            {/* Rows per page, and the pager beside it.
                The pager and the "showing" line are dropped when every pattern fits on one
                page, but the row count stays: it is how someone GETS a second page, so
                hiding it at size 50 would strand them there. */}
            {patterns.length > 0 && (
              <Box padding="400" borderBlockStart="border">
                <InlineStack align="space-between" blockAlign="center">
                  <PageSizeSelect value={patternsPageSize} onChange={handlePatternsPageSizeChange} />
                  {pagination.totalPages > 1 && (
                    <InlineStack gap="400" blockAlign="center">
                      <Text variant="bodySm" tone="subdued">
                        Showing {Math.max(1, ((currentPage - 1) * patternsPageSize) + 1)}–{Math.min(currentPage * patternsPageSize, pagination.totalCount || 0)} of {(pagination.totalCount || 0).toLocaleString()} patterns
                      </Text>
                      <InlineStack gap="200">
                        <Button
                          disabled={currentPage <= 1 || isLoading}
                          onClick={() => setCurrentPage(currentPage - 1)}
                          size="micro"
                        >
                          Previous
                        </Button>
                        <Text variant="bodySm">
                          Page {currentPage} of {pagination.totalPages}
                        </Text>
                        <Button
                          disabled={currentPage >= pagination.totalPages || isLoading || isPlaceholderData}
                          onClick={() => setCurrentPage(currentPage + 1)}
                          size="micro"
                        >
                          Next
                        </Button>
                      </InlineStack>
                    </InlineStack>
                  )}
                </InlineStack>
              </Box>
            )}
          </Card>
        </Layout.Section>
        )}

        {/* Per-brand override filters */}
        {selectedTab === 1 && (
        <Layout.Section>
          <Card>
            <Box padding="300">
              <InlineStack gap="300" align="start" blockAlign="end">
                <Box width="260px">
                  <TextField
                    label="Search retailer"
                    value={overrideSearch}
                    onChange={(value) => { setOverrideSearch(value); setOverridePage(1); }}
                    placeholder="Search retailer name..."
                    prefix={<SearchIcon />}
                    clearButton
                    onClearButtonClick={() => { setOverrideSearch(''); setOverridePage(1); }}
                    autoComplete="off"
                  />
                </Box>

                <Box width="240px">
                  <Select
                    label="Brand"
                    options={brandFilterOptions}
                    value={overrideBrandFilter}
                    onChange={(value) => { setOverrideBrandFilter(value); setOverridePage(1); }}
                  />
                </Box>

                <Box width="180px">
                  <Select
                    label="Action"
                    options={overrideActionFilterOptions}
                    value={overrideActionFilter}
                    onChange={(value) => { setOverrideActionFilter(value); setOverridePage(1); }}
                  />
                </Box>

                <Box width="150px">
                  <Select
                    label="Status"
                    options={[
                      { label: 'All', value: '' },
                      { label: 'Active', value: 'true' },
                      { label: 'Inactive', value: 'false' },
                    ]}
                    value={overrideStatusFilter}
                    onChange={(value) => { setOverrideStatusFilter(value); setOverridePage(1); }}
                  />
                </Box>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>
        )}

        {/* Per-brand override table */}
        {selectedTab === 1 && (
        <Layout.Section>
          {overridesError && (
            <Box paddingBlockEnd="300">
              <Banner tone="critical">
                <Text>Unable to load per-brand overrides. {overridesError}</Text>
              </Banner>
            </Box>
          )}
          <Card>
            {overridesLoading && overrides.length === 0 ? (
              <Box padding="800">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <Text variant="bodyLg" fontWeight="medium">Loading per-brand overrides...</Text>
                </InlineStack>
              </Box>
            ) : (
              /* Same treatment as the patterns table above: the headings stay and the empty
                 state becomes a single full-width row, so the nine columns an override is
                 made of are visible before the first one exists. The spinner branch above is
                 still a branch — a table cannot show "loading" and its columns at once
                 without claiming the empty row is data. */
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text', 'text']}
                headings={[
                  'Brand',
                  'Retailer',
                  'Action',
                  'Pattern',
                  'Type',
                  'Priority',
                  'Expires',
                  'Status',
                  'Actions',
                ]}
                rows={overrides.length === 0 ? [[
                  <EmptyState
                    key="no-overrides"
                    heading="No per-brand overrides yet"
                    content="Every brand currently uses the global patterns. Add an override when one brand's order tags need different handling — for example when its orders are landing in the Others bucket and being billed at the wrong rate."
                    image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
                    action={{
                      content: 'Add Override',
                      onAction: handleAddOverride,
                    }}
                  />,
                ]] : overrideRows}
                loading={overridesLoading}
              />
            )}

            {/* Same footer as the patterns table above: the row count is always available
                once there are rows, the pager only once there is more than one page. */}
            {overrides.length > 0 && (
              <Box padding="400" borderBlockStart="border">
                <InlineStack align="space-between" blockAlign="center">
                  <PageSizeSelect value={overridesPageSize} onChange={handleOverridesPageSizeChange} />
                  {overridesPagination.totalPages > 1 && (
                    <InlineStack gap="400" blockAlign="center">
                      <Text variant="bodySm" tone="subdued">
                        Showing {Math.max(1, ((overridePage - 1) * overridesPageSize) + 1)}–{Math.min(overridePage * overridesPageSize, overridesPagination.totalCount || 0)} of {(overridesPagination.totalCount || 0).toLocaleString()} overrides
                      </Text>
                      <InlineStack gap="200">
                        <Button
                          disabled={overridePage <= 1 || overridesLoading}
                          onClick={() => setOverridePage(overridePage - 1)}
                          size="micro"
                        >
                          Previous
                        </Button>
                        <Text variant="bodySm">
                          Page {overridePage} of {overridesPagination.totalPages}
                        </Text>
                        <Button
                          disabled={overridePage >= overridesPagination.totalPages || overridesLoading}
                          onClick={() => setOverridePage(overridePage + 1)}
                          size="micro"
                        >
                          Next
                        </Button>
                      </InlineStack>
                    </InlineStack>
                  )}
                </InlineStack>
              </Box>
            )}
          </Card>
        </Layout.Section>
        )}
      </Layout>

      {/* Add/Edit Pattern Modal */}
      <Modal
        open={showPatternModal}
        onClose={() => setShowPatternModal(false)}
        title={editingPattern ? 'Edit Marketplace Pattern' : 'Add Marketplace Pattern'}
        primaryAction={{
          content: editingPattern ? 'Update Pattern' : 'Create Pattern',
          onAction: handleSavePattern,
          loading: createPattern.isPending || updatePattern.isPending,
          // Refuse to write a pattern until the server's PATTERN_TYPES and priority range have
          // loaded. This form decides retailer attribution and therefore the commission rate
          // billed; it must not save against a client-side guess.
          disabled: !optionsLoaded,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowPatternModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text tone="subdued" variant="bodyMd">
              Create marketplace detection patterns to automatically identify which marketplace an order came from based on order tags.
            </Text>

            {/* The pattern types and priority range come from the server. If that call failed we
                say so and block the save, rather than silently offering a local guess of an
                enum that decides commission attribution. */}
            {!optionsLoaded && (
              <Banner tone={optionsError ? 'critical' : 'info'}>
                <Text variant="bodySm">
                  {optionsError
                    ? 'Could not load the pattern types and priority range from the server, so this form cannot be saved. Reload the page to try again.'
                    : 'Loading pattern types and priority range from the server…'}
                </Text>
              </Banner>
            )}

            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Marketplace Name"
                  value={formData.marketplaceName}
                  onChange={(value) => setFormData({ ...formData, marketplaceName: value })}
                  placeholder="e.g., Target, Amazon, Macy's"
                  helpText="The name of the marketplace (e.g., Target, Amazon, Macy's)"
                  requiredIndicator
                />
                <TextField
                  label="Display Name"
                  value={formData.displayName}
                  onChange={(value) => setFormData({ ...formData, displayName: value })}
                  placeholder="Leave blank to use marketplace name"
                  helpText="Optional custom display name. If left blank, marketplace name will be used."
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <Select
                  label="Pattern Type"
                  options={patternTypeOptions}
                  value={formData.patternType}
                  onChange={(value) => setFormData({ ...formData, patternType: value })}
                  helpText="How the pattern should match order tags"
                  requiredIndicator
                />
                <TextField
                  label="Pattern Value"
                  value={formData.patternValue}
                  onChange={(value) => setFormData({ ...formData, patternValue: value })}
                  placeholder="e.g., target, amazon-fba, walmart.com"
                  helpText={getPatternHelpText(formData.patternType)}
                  requiredIndicator
                />
              </FormLayout.Group>

              {/* Both fields were commented out while the table still rendered a
                  Priority column and a Category badge — so an admin could see values
                  they had no way to set, and every pattern created through this form
                  was hard-coded priority 1.

                  Priority is not cosmetic. When two patterns could both match an order
                  tag, priority decides which one wins, which decides the retailer the
                  order is attributed to, and therefore the commission rate it is
                  billed at. With every pattern at 1 that tie is unresolvable. */}
              <FormLayout.Group>
                <TextField
                  label="Priority"
                  type="number"
                  value={formData.priority.toString()}
                  onChange={(value) => {
                    // Bounds come from the server's PRIORITY_MIN/PRIORITY_MAX, the same values
                    // the override form on the other tab already used.
                    const numValue = parseInt(value, 10);
                    const safeValue = isNaN(numValue) || numValue < priorityMin || numValue > priorityMax
                      ? priorityMin
                      : numValue;
                    setFormData({ ...formData, priority: safeValue });
                  }}
                  placeholder={`${priorityMin}-${priorityMax}`}
                  helpText="Higher numbers are checked first. When two patterns could match the same order tag, the higher priority decides which retailer the order is attributed to — and so which commission rate applies."
                  min={priorityMin}
                  max={priorityMax}
                  autoComplete="off"
                />
                <Select
                  label="Category"
                  options={categoryOptions}
                  value={formData.category}
                  onChange={(value) => setFormData({ ...formData, category: value })}
                  helpText="Primary (main patterns), Alias (alternative names), Legacy (older patterns). Organisational only — it does not affect matching."
                />
              </FormLayout.Group>

              <TextField
                label="Description"
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                multiline={2}
                placeholder="e.g., Matches Target orders with standard tagging format"
                helpText="Internal notes about this pattern for other administrators"
              />
            </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Add/Edit Per-Brand Override Modal */}
      <Modal
        open={showOverrideModal}
        onClose={() => setShowOverrideModal(false)}
        title={editingOverride ? 'Edit Per-Brand Override' : 'Add Per-Brand Override'}
        primaryAction={{
          content: editingOverride ? 'Update Override' : 'Create Override',
          onAction: handleSaveOverride,
          loading: savingOverride,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowOverrideModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text tone="subdued" variant="bodyMd">
              An override changes marketplace detection for one brand only. Every other brand keeps
              using the global patterns.
            </Text>

            {isDisableAction && (
              <Banner tone="warning">
                <Text>
                  Disabling stops this brand matching the selected retailer altogether — its orders
                  will fall through to whatever else matches, or to the &ldquo;Others&rdquo; bucket.
                  Because overrides apply per retailer name, this disables that retailer&rsquo;s
                  global patterns for this brand, not just the one pattern you picked.
                </Text>
              </Banner>
            )}

            <FormLayout>
              <FormLayout.Group>
                <Select
                  label="Brand"
                  options={brandOptions}
                  value={overrideForm.brandId}
                  onChange={(value) => setOverrideForm({ ...overrideForm, brandId: value })}
                  helpText={editingOverride
                    ? 'A brand cannot be changed on an existing override — delete it and create one for the other brand.'
                    : 'The override applies to this brand only.'}
                  disabled={Boolean(editingOverride)}
                  requiredIndicator
                />
                <Select
                  label="Override Action"
                  options={overrideActionOptions}
                  value={overrideForm.overrideAction}
                  onChange={(value) => setOverrideForm({ ...overrideForm, overrideAction: value })}
                  helpText="Disable is how a brand opts out of a global pattern. The others all add or replace matching for this brand."
                  requiredIndicator
                />
              </FormLayout.Group>

              <Select
                label="Global Pattern"
                options={globalPatternOptions}
                value={overrideForm.globalPatternId}
                onChange={(value) => setOverrideForm({ ...overrideForm, globalPatternId: value })}
                helpText={needsGlobalPattern
                  ? `"${overrideForm.overrideAction}" acts on an existing global pattern, so one must be selected. The retailer name is taken from it.`
                  : 'Optional. Link the global pattern this override relates to, or leave empty to introduce a retailer the global patterns do not cover.'}
                requiredIndicator={needsGlobalPattern}
              />

              {!overrideForm.globalPatternId && (
                <FormLayout.Group>
                  <TextField
                    label="Retailer Name"
                    value={overrideForm.marketplaceName}
                    onChange={(value) => setOverrideForm({ ...overrideForm, marketplaceName: value })}
                    placeholder="e.g., Target, Macy's, Kohl's"
                    helpText="The retailer this brand's orders should be attributed to. Must match the global spelling exactly to override an existing retailer."
                    autoComplete="off"
                    requiredIndicator
                  />
                  <TextField
                    label="Display Name"
                    value={overrideForm.displayName}
                    onChange={(value) => setOverrideForm({ ...overrideForm, displayName: value })}
                    placeholder="Leave blank to use the retailer name"
                    helpText="Optional label for this override in the console."
                    autoComplete="off"
                  />
                </FormLayout.Group>
              )}

              {!isDisableAction && (
                <FormLayout.Group>
                  <Select
                    label="Pattern Type"
                    options={overridePatternTypeOptions}
                    value={overrideForm.patternType}
                    onChange={(value) => setOverrideForm({ ...overrideForm, patternType: value })}
                    helpText="How the pattern should match this brand's order tags"
                    requiredIndicator
                  />
                  <TextField
                    label="Pattern Value"
                    value={overrideForm.patternValue}
                    onChange={(value) => setOverrideForm({ ...overrideForm, patternValue: value })}
                    placeholder="e.g., target, macys-dropship, kohls.com"
                    helpText={getPatternHelpText(overrideForm.patternType)}
                    autoComplete="off"
                    requiredIndicator
                  />
                </FormLayout.Group>
              )}

              {/* Priority reads priorityMin/priorityMax, NOT overrideOptions.priorityRange.

                  priorityRange starts null and only becomes an object if the options request
                  succeeds. These props are evaluated when this JSX is CREATED, which happens on
                  every render of the page whether or not the modal is open — so reading .min off
                  the null threw during render and the whole page fell to the error boundary.
                  optionsLoaded guards the save button and shows a banner, but the fields are
                  constructed before either can help. */}
              <FormLayout.Group>
                <TextField
                  label="Priority"
                  type="number"
                  value={String(overrideForm.priority)}
                  onChange={(value) => {
                    const numValue = parseInt(value, 10);
                    const safeValue = Number.isNaN(numValue) || numValue < priorityMin || numValue > priorityMax
                      ? priorityMin
                      : numValue;
                    setOverrideForm({ ...overrideForm, priority: safeValue });
                  }}
                  placeholder={`${priorityMin}-${priorityMax}`}
                  helpText={`Higher numbers are checked first (${priorityMin}-${priorityMax}). Priority decides which retailer wins a tie — and so which commission rate the order is billed at.`}
                  min={priorityMin}
                  max={priorityMax}
                  autoComplete="off"
                />
                <Checkbox
                  label="Case sensitive"
                  checked={overrideForm.isCaseSensitive}
                  onChange={(value) => setOverrideForm({ ...overrideForm, isCaseSensitive: value })}
                  helpText="Off means tags match regardless of capitalisation, which is what most retailer tags need."
                  disabled={isDisableAction}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <Checkbox
                  label="Active"
                  checked={overrideForm.isActive}
                  onChange={(value) => setOverrideForm({ ...overrideForm, isActive: value })}
                  helpText="Inactive overrides are ignored by detection — the brand falls back to the global patterns."
                />
                <Checkbox
                  label="Temporary (expires)"
                  checked={overrideForm.isTemporary}
                  onChange={(value) => setOverrideForm({
                    ...overrideForm,
                    isTemporary: value,
                    expiresAt: value ? overrideForm.expiresAt : '',
                  })}
                  helpText="Use for a stop-gap fix while the global pattern is corrected."
                />
              </FormLayout.Group>

              {overrideForm.isTemporary && (
                <TextField
                  label="Expires On"
                  type="date"
                  value={overrideForm.expiresAt}
                  onChange={(value) => setOverrideForm({ ...overrideForm, expiresAt: value })}
                  helpText="Must be in the future. After this date the brand goes back to the global patterns automatically."
                  autoComplete="off"
                  requiredIndicator
                />
              )}

              <TextField
                label="Reason"
                value={overrideForm.reason}
                onChange={(value) => setOverrideForm({ ...overrideForm, reason: value })}
                multiline={2}
                placeholder="e.g., This brand tags Macy's orders as 'macys-dd' so the global EXACT pattern misses them and they bill as Others"
                helpText={isDisableAction
                  ? 'Required when disabling — this is the audit trail for a change that alters how the brand is billed.'
                  : 'Why this override exists, for the next administrator who finds it.'}
                autoComplete="off"
                requiredIndicator={isDisableAction}
              />
            </FormLayout>

            {editingOverride && (
              <Text tone="subdued" variant="bodySm">
                Created by {editingOverride.createdBy || 'unknown'}
                {editingOverride.createdAt ? ` on ${new Date(editingOverride.createdAt).toLocaleDateString()}` : ''}
                {editingOverride.lastModifiedBy ? ` · last changed by ${editingOverride.lastModifiedBy}` : ''}
                {editingOverride.lastModifiedAt ? ` on ${new Date(editingOverride.lastModifiedAt).toLocaleDateString()}` : ''}
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}